import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface Board {
  id: string;
  name: string;
  icon?: string;
}

interface BoardSelectorProps {
  boards: Board[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  placeholder?: string;
  className?: string;
  showSummary?: boolean;    // NEW: Show "X of Y selected" below selector
  showSelectAll?: boolean;  // NEW: Show "Select All" checkbox in dropdown
}

export function BoardSelector({
  boards,
  selectedIds,
  onSelectionChange,
  placeholder = "Select boards",
  className,
  showSummary = false,    // Default to false for backward compatibility
  showSelectAll = false   // Default to false for backward compatibility
}: BoardSelectorProps) {
  const [open, setOpen] = useState(false);
  
  // Debug logging
  console.log('🎯 BoardSelector Debug:', {
    boards: boards,
    boardCount: boards.length,
    selectedIds: selectedIds,
    selectedCount: selectedIds.length,
    placeholder: placeholder
  });

  const toggleBoard = (boardId: string) => {
    if (selectedIds.includes(boardId)) {
      onSelectionChange(selectedIds.filter(id => id !== boardId));
    } else {
      onSelectionChange([...selectedIds, boardId]);
    }
  };

  const toggleAll = () => {
    // If all are selected, unselect all
    // If some or none are selected, select all
    if (selectedIds.length === boards.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(boards.map(b => b.id));
    }
  };
  
  const getDisplayText = () => {
    // Determine the type based on placeholder
    const type = placeholder.toLowerCase().includes('role') ? 'role boards' : 'locations';
    
    // Always show count format
    if (selectedIds.length === boards.length && boards.length > 0) {
      // When all are selected, show "All [type]"
      return `All ${type}`;
    }
    
    // Show count format for any other selection (including 0)
    return `${selectedIds.length}/${boards.length} ${type} selected`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800 hover:text-white min-w-[280px]",
            className
          )}
        >
          <span className="truncate">{getDisplayText()}</span>
          <span className="mdi mdi-chevron-down ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
        <PopoverContent className="w-[300px] max-w-[calc(100vw-2rem)] p-0 bg-neutral-900 border-neutral-700">
          <div className="p-2 border-b border-neutral-700">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-white hover:bg-neutral-800 hover:text-white"
              onClick={toggleAll}
            >
              <div className={cn(
                "mr-2 h-4 w-4 shrink-0 border rounded flex items-center justify-center",
                selectedIds.length === boards.length 
                  ? "bg-blue-600 border-blue-600" 
                  : "border-neutral-500 bg-transparent"
              )}>
                {selectedIds.length === boards.length && (
                  <span className="mdi mdi-check text-xs text-white" />
                )}
              </div>
              Select All
            </Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {boards.map((board) => (
              <Button
                key={board.id}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-white hover:bg-neutral-800 hover:text-white mb-1 h-auto min-h-8 whitespace-normal text-left"
                onClick={() => toggleBoard(board.id)}
              >
                <div className={cn(
                  "mr-2 h-4 w-4 shrink-0 border rounded flex items-center justify-center",
                  selectedIds.includes(board.id) 
                    ? "bg-blue-600 border-blue-600" 
                    : "border-neutral-500 bg-transparent"
                )}>
                  {selectedIds.includes(board.id) && (
                    <span className="mdi mdi-check text-xs text-white" />
                  )}
                </div>
                <span className="min-w-0 break-words">{board.name}</span>
              </Button>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <div className="p-2 border-t border-neutral-700">
              <div className="flex flex-wrap gap-1">
                {selectedIds.map(id => {
                  const board = boards.find(b => b.id === id);
                  return board ? (
                    <Badge 
                      key={id} 
                      variant="outline" 
                      className="text-xs bg-transparent border-orange-500 text-orange-400 hover:bg-orange-500/10 rounded-full px-2 py-1 max-w-full break-words"
                    >
                      {board.name}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
  );
} 